/**
 * Browser wishlist: product handles saved in localStorage.
 * Any element with [data-wishlist-handle] toggles that product; [data-wishlist-count] shows the total.
 * Other scripts can use window.ObravenWishlist and listen for the `wishlist:change` event on document.
 */
(() => {
  const STORAGE_KEY = 'obraven:wishlist';
  const config = window.obravenWishlist || {};

  const read = () => {
    try {
      const list = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(list) ? list.filter((handle) => typeof handle === 'string') : [];
    } catch (error) {
      return [];
    }
  };

  const write = (list) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (error) {
      // Storage can be unavailable (private mode); the button simply won't persist.
    }
  };

  const sync = () => {
    const list = read();
    document.querySelectorAll('[data-wishlist-handle]').forEach((button) => {
      const saved = list.includes(button.dataset.wishlistHandle);
      button.setAttribute('aria-pressed', String(saved));
      const label = saved ? button.dataset.labelRemove : button.dataset.labelAdd;
      if (label) button.setAttribute('aria-label', label);
    });
    document.querySelectorAll('[data-wishlist-count]').forEach((element) => {
      // Only write on change: a text write is a DOM mutation and would re-trigger the observer below.
      if (element.textContent !== String(list.length)) element.textContent = String(list.length);
      element.hidden = list.length === 0;
    });
  };

  const change = (list) => {
    write(list);
    sync();
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { list } }));
  };

  window.ObravenWishlist = {
    get: read,
    has: (handle) => read().includes(handle),
    add: (handle) => {
      const list = read();
      if (!list.includes(handle)) change([handle, ...list]);
    },
    remove: (handle) => change(read().filter((item) => item !== handle)),
    sync,
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wishlist-handle]');
    if (!button) return;
    event.preventDefault();

    if (config.requireLogin && !config.loggedIn) {
      window.location.href = config.loginUrl;
      return;
    }

    const handle = button.dataset.wishlistHandle;
    if (window.ObravenWishlist.has(handle)) {
      window.ObravenWishlist.remove(handle);
    } else {
      window.ObravenWishlist.add(handle);
    }
  });

  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) sync();
  });

  // Buttons also arrive later through section re-renders (cart, filters, recommendations). Only react to
  // added nodes that contain wishlist controls: Shopify's own scripts mutate the page constantly.
  const WISHLIST_SELECTOR = '[data-wishlist-handle], [data-wishlist-count]';
  let pending = false;
  new MutationObserver((mutations) => {
    if (pending) return;
    const relevant = mutations.some((mutation) =>
      [...mutation.addedNodes].some(
        (node) => node.nodeType === 1 && (node.matches(WISHLIST_SELECTOR) || node.querySelector(WISHLIST_SELECTOR))
      )
    );
    if (!relevant) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      sync();
    });
  }).observe(document.body || document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }
})();
